import { useEffect } from "react";
import { useSelector, useStore } from "react-redux";

import { takeUploadedPdf } from "../aui";
import { getWorkspaceFileBytes } from "../sandbox/client/api";
import { agentActions, type AgentState, type OutgoingFile } from "../store/agentSlice";
import { threadFilePages } from "./files";
import {
  PAGE_COUNT_TIMEOUT_MS,
  pdfPageCount,
  requestTimedOut,
  TIMED_OUT,
  type PageCount,
} from "./pages";

/**
 * A draft PDF this tab has no page count for yet: its pages' default (see PagesPicker) waits for
 * one, and so does the draft's send.
 */
export const uncounted = (agent: AgentState, file: OutgoingFile) =>
  file.contentType === "application/pdf" && agent.pdfPages[file.id] === undefined;

/**
 * A draft PDF's pages, from where this tab can get them: pdf.js on the upload it still holds or on
 * the Files panel file it was added from, else the server's count of the thread file (its link is
 * a presigned one, which the app can't fetch). TIMED_OUT when the bytes take too long to come too.
 */
async function draftPdfPages(
  agent: AgentState,
  threadId: number,
  fileId: number
): Promise<PageCount> {
  const upload = takeUploadedPdf(fileId);
  if (upload) return pdfPageCount(await upload.arrayBuffer());
  const key = Object.entries(agent.workspaceFiles[threadId] ?? {}).find(
    ([, added]) => added === fileId
  )?.[0];
  if (!key) return threadFilePages(threadId, fileId);
  const at = key.indexOf(":"); // `workspaceFileKey`: the workspace, then the path
  let bytes: ArrayBuffer;
  try {
    bytes = await getWorkspaceFileBytes(key.slice(0, at), key.slice(at + 1), {
      timeout: PAGE_COUNT_TIMEOUT_MS,
    });
  } catch (error) {
    return requestTimedOut(error) ? TIMED_OUT : null;
  }
  return pdfPageCount(bytes);
}

/**
 * Counts the pages of every PDF on the thread's draft that this tab has no count for, wherever it
 * came from: an upload, the Files panel, an @ file or document, or a draft loaded with the thread.
 * Not counted (the bytes or the server's count don't come, or the PDF does not open), it has none:
 * all pages.
 */
export function useCountDraftPdfs(threadId: number | null) {
  const store = useStore<{ agent: AgentState }>();
  // Joined, so the effect runs when the list changes, not on every store change.
  const waiting = useSelector(({ agent }: { agent: AgentState }) =>
    threadId == null
      ? ""
      : (agent.threads[threadId]?.composer.files ?? [])
          .filter((file) => uncounted(agent, file) && !agent.pdfCounting[file.id])
          .map((file) => file.id)
          .join(",")
  );

  useEffect(() => {
    if (threadId == null || !waiting) return;
    for (const fileId of waiting.split(",").map(Number)) {
      const { agent } = store.getState();
      // Another view of the thread may have started it.
      if (agent.pdfCounting[fileId] || agent.pdfPages[fileId] !== undefined) continue;
      store.dispatch(agentActions.pdfPagesCounting({ fileId }));
      const counted = (pages: PageCount) =>
        store.dispatch(agentActions.pdfPagesCounted({ fileId, pages }));
      void draftPdfPages(agent, threadId, fileId).then(counted, () => counted(null));
    }
  }, [store, threadId, waiting]);
}
